import { useCallback } from 'react'

import { queryOptions, useQuery } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'
import { FILE_STALE_TIME } from '@/constants/file'

import { buildPaginatedEndpoint } from '@/helpers/api'

import { useSetFileCache } from '@/hooks/files/useFile'
import { pursuitFilesPaginatedQueryKeys } from '@/hooks/pursuits/types'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { PaginateRequest, PaginatedResponse } from '@/types/Api'
import type { File } from '@/types/File'
import type { UUID } from '@/types/Id'

interface UsePursuitFilesArgs<T> {
  pursuitId: UUID | ''
  enabled?: boolean
  select?: (data: PaginatedResponse<File>) => T
  loadAllPages?: boolean
  pageLimit?: number
}

export const fetchPursuitFiles = async (
  pursuitId: UUID | '',
  accessToken: string,
  pagination: PaginateRequest,
): Promise<PaginatedResponse<File>> => {
  if (!pursuitId) {
    return { count: 0, has_more: false, data: [] }
  }
  const url = buildPaginatedEndpoint(
    `${API_URL}/api/v1/pursuits/${pursuitId}/files`,
    pagination,
  )
  const res: Response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
  if (!res.ok) {
    throw new Error(res.statusText)
  }
  return (await res.json()) as PaginatedResponse<File>
}

type UsePursuitFilesQueryOptionsArgs = Omit<
  UsePursuitFilesArgs<never>,
  'select'
>

export const usePursuitFilesQueryOptions = ({
  pursuitId,
  enabled = true,
  loadAllPages = false,
  pageLimit = 10000,
}: UsePursuitFilesQueryOptionsArgs) => {
  const { accessToken } = useAuthInfoWithThrow()

  const setFileCache = useSetFileCache()

  return queryOptions({
    queryKey: loadAllPages
      ? pursuitFilesPaginatedQueryKeys.allPages({ pursuitId, pageLimit })
      : pursuitFilesPaginatedQueryKeys.singlePage({ pursuitId, pageLimit }),
    queryFn: async () => {
      // A pursuit should generally never hold more than 10,000 files, so
      // every page fits in one request.
      const files = await fetchPursuitFiles(pursuitId, accessToken, {
        limit: loadAllPages ? 10000 : pageLimit,
      })
      setFileCache(files.data)
      return files
    },
    staleTime: FILE_STALE_TIME,
    enabled: enabled && !!pursuitId,
  })
}

export const usePursuitFiles = <T = File[]>({
  pursuitId,
  enabled = true,
  select,
  loadAllPages = false,
  pageLimit = 10000,
}: UsePursuitFilesArgs<T>) => {
  const options = usePursuitFilesQueryOptions({
    pursuitId,
    enabled,
    loadAllPages,
    pageLimit,
  })

  const selector = useCallback(
    (data: PaginatedResponse<File>) => {
      if (select) {
        return select(data)
      }
      return data.data as unknown as T
    },
    [select],
  )

  return useQuery({ ...options, select: selector })
}

interface UsePursuitFileIdsArgs {
  pursuitId: UUID | ''
}

const selectPursuitFileids = (data: PaginatedResponse<File>) => {
  return data.data.map((file) => file.id)
}

export const usePursuitFileIds = <T = UUID[]>({
  pursuitId,
}: UsePursuitFileIdsArgs) => {
  return usePursuitFiles({ pursuitId, select: selectPursuitFileids })
}
